/**
 * Configuração da execução técnica de um item de modelo "Execução da O.S.":
 * tipo, campos (com unidade e limites), intervenções aplicáveis, posições e exigências.
 * Nada é fixo no código — pneu, freio ou óleo são só configurações diferentes.
 */
import type { MaintenanceExecFieldDto, MaintenanceExecutionConfigDto, MaintenanceInterventionDto } from "@bi/types";
import { slugCode } from "./template-editor";
import { ALL_INTERVENTIONS, INTERVENTION_LABEL } from "./work-order-inspection-ui";
import { TIPO_EIXO } from "./ExecutionScreens";
const ICONES = ["engine", "gearbox", "differential", "filter-oil", "filter-air", "filter-fuel", "filter-water", "filter-adblue", "bearing", "hub", "seal", "brake", "grease", "axle", "wrench", "tire", "cooling", "suspension", "light", "generic"];

type Tipo = "simple_check" | MaintenanceExecutionConfigDto["type"];

const TIPOS: Array<{ value: Tipo; label: string }> = [
  { value: "simple_check", label: "✓ simples (feito / não feito)" },
  { value: "component", label: "Componente (condição + intervenção)" },
  { value: "measurement", label: "Medição / registro (campos)" },
  { value: "positional", label: "Por posição (eixo / lado)" },
  { value: "tire_map", label: "Mapa de pneus do veículo" },
];

const CAMPOS: Array<{ value: MaintenanceExecFieldDto["type"]; label: string }> = [
  { value: "measurement", label: "Medida (número com limites)" },
  { value: "number", label: "Número" },
  { value: "text", label: "Texto" },
  { value: "choice", label: "Uma opção" },
  { value: "multichoice", label: "Várias opções" },
  { value: "boolean", label: "Sim / não" },
  { value: "date", label: "Data" },
];

export function ExecutionEditor({
  value, onChange,
}: { value: MaintenanceExecutionConfigDto | undefined; onChange: (v: MaintenanceExecutionConfigDto | undefined) => void }) {
  const tipo: Tipo = value?.type ?? "simple_check";
  const set = (p: Partial<MaintenanceExecutionConfigDto>) => value && onChange({ ...value, ...p });
  const trocar = (t: Tipo) => {
    if (t === "simple_check") return onChange(undefined);
    const base: MaintenanceExecutionConfigDto = value ?? { type: t, fields: [] };
    onChange({
      ...base, type: t,
      positions: t === "positional" ? (base.positions?.mode === "tire_map" || !base.positions ? { mode: "axles", axles: 2 } : base.positions)
        : t === "tire_map" ? { mode: "tire_map" } : undefined,
    });
  };
  const campo = (i: number, p: Partial<MaintenanceExecFieldDto>) => set({ fields: value!.fields.map((f, j) => (j === i ? { ...f, ...p } : f)) });
  const novoCampo = () => {
    const usados = new Set(value!.fields.map((f) => f.name));
    let nome = "campo"; let n = 2;
    while (usados.has(nome)) nome = `campo_${n++}`;
    set({ fields: [...value!.fields, { name: nome, label: "", type: "measurement" }] });
  };

  return (
    <div className="sm:col-span-3 space-y-2 rounded-lg border border-tm-100 bg-tm-50/30 p-3">
      <label className="block">
        <span className="text-xs font-medium text-gray-600">Execução na O.S.</span>
        <select className="input w-full" value={tipo} onChange={(e) => trocar(e.target.value as Tipo)}>
          {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </label>

      {value && value.type === "positional" && (
        <label className="block">
          <span className="text-xs text-gray-500">Apresentação na tela</span>
          <select className="input w-full" value={value.layout ?? "default"} onChange={(e) => set({ layout: e.target.value as "default" | "tiles" | "axle_map" })}>
            <option value="default">Mapa de posições e formulário</option>
            <option value="tiles">Cartões com ícone ("o que foi trocado?")</option>
            <option value="axle_map">Desenho do veículo por eixo (serviços por eixo e lado)</option>
          </select>
        </label>
      )}

      {value && (
        <>
          {value.type === "positional" && (
            <div className="flex flex-wrap items-end gap-3 text-sm">
              <label className="block"><span className="text-xs text-gray-500">Posições</span>
                <select className="input" value={value.positions?.mode ?? "axles"}
                  onChange={(e) => set({ positions: e.target.value === "list" ? { mode: "list", items: [{ code: "P1", label: "Posição 1" }] }
                    : e.target.value === "vehicle_axles" ? { mode: "vehicle_axles", vehicle: "main" } : { mode: "axles", axles: 2 } })}>
                  <option value="axles">Por eixo (LE / LD)</option><option value="list">Lista própria</option>
                  <option value="vehicle_axles">Eixos do veículo (direcional, tração, livre)</option>
                </select></label>
              {value.positions?.mode === "axles" && (
                <>
                  <label className="block"><span className="text-xs text-gray-500">Eixos</span>
                    <input className="input w-20" inputMode="numeric" value={value.positions.axles}
                      onChange={(e) => set({ positions: { mode: "axles", axles: Math.min(9, Math.max(1, Number(e.target.value.replace(/\D/g, "")) || 1)), dual: (value.positions as { dual?: boolean }).dual } })} /></label>
                  <label className="inline-flex items-center gap-1.5 pb-2 text-xs text-gray-600">
                    <input type="checkbox" checked={!!value.positions.dual} onChange={(e) => set({ positions: { mode: "axles", axles: (value.positions as { axles: number }).axles, dual: e.target.checked } })} /> Rodas duplas
                  </label>
                </>
              )}
              {value.positions?.mode === "vehicle_axles" && (
                <label className="block"><span className="text-xs text-gray-500">De qual veículo</span>
                  <select className="input" value={value.positions.vehicle ?? "main"} onChange={(e) => set({ positions: { mode: "vehicle_axles", vehicle: e.target.value as "main" | "trailer" } })}>
                    <option value="main">Da placa da O.S.</option><option value="trailer">Da carreta vinculada</option>
                  </select></label>
              )}
              {value.positions?.mode === "list" && (
                <label className="block min-w-[240px] flex-1"><span className="text-xs text-gray-500">Uma por linha: código;nome</span>
                  <textarea className="input w-full" rows={3} value={value.positions.items.map((i) => `${i.code};${i.label}`).join("\n")}
                    onChange={(e) => set({ positions: { mode: "list", items: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [c, ...r] = l.split(";"); return { code: (c ?? "").trim().replace(/[^A-Za-z0-9_-]/g, "").slice(0, 30), label: (r.join(";") || c || "").trim() }; }).filter((i) => i.code) } })} /></label>
              )}
            </div>
          )}
          {value.type === "tire_map" && <p className="text-xs text-gray-500">As posições vêm do mapa de pneus do veículo; cada posição guarda o pneu.</p>}

          <div className="space-y-2">
            <span className="text-xs font-medium text-gray-600">Campos</span>
            {value.fields.map((f, i) => (
              <div key={f.name} className="space-y-1.5 rounded-md border border-surface-border bg-white p-2">
                <div className="flex flex-wrap items-center gap-2">
                  <input className="input min-w-[140px] flex-1" placeholder="Nome (ex.: Espessura)" value={f.label}
                    onChange={(e) => campo(i, { label: e.target.value, name: f.name.startsWith("campo") ? slugCode(e.target.value || f.name, 40).replace(/^x_/, "c_") : f.name })} />
                  <select className="input w-48" value={f.type} onChange={(e) => campo(i, { type: e.target.value as MaintenanceExecFieldDto["type"] })}>
                    {CAMPOS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <label className="inline-flex items-center gap-1 text-xs text-gray-600"><input type="checkbox" checked={!!f.required} onChange={(e) => campo(i, { required: e.target.checked })} /> Obrigatório</label>
                  <button type="button" className="btn-ghost text-xs text-danger-600" aria-label="Remover campo" onClick={() => set({ fields: value.fields.filter((_, j) => j !== i) })}>×</button>
                </div>
                {(f.type === "number" || f.type === "measurement") && (
                  <div className="flex flex-wrap items-end gap-2 text-xs">
                    <Num rotulo="Unidade" texto valor={f.unit} onChange={(v) => campo(i, { unit: v ? String(v) : undefined })} largura="w-16" />
                    <Num rotulo="Mín." valor={f.min} onChange={(v) => campo(i, { min: v as number | undefined })} />
                    <Num rotulo="Máx." valor={f.max} onChange={(v) => campo(i, { max: v as number | undefined })} />
                    <label className="block"><span className="text-gray-500">Sentido</span>
                      <select className="input" value={f.direction ?? "higher_is_better"} onChange={(e) => campo(i, { direction: e.target.value as "higher_is_better" | "lower_is_better" })}>
                        <option value="higher_is_better">Maior é melhor</option><option value="lower_is_better">Menor é melhor</option>
                      </select></label>
                    <Num rotulo="Limite de atenção" valor={f.warningThreshold} onChange={(v) => campo(i, { warningThreshold: v as number | undefined })} />
                    <Num rotulo="Limite crítico" valor={f.criticalThreshold} onChange={(v) => campo(i, { criticalThreshold: v as number | undefined })} />
                  </div>
                )}
                {(f.type === "choice" || f.type === "multichoice") && (
                  <div className="space-y-1.5 text-xs">
                    <span className="text-gray-500">Opções{value.positions?.mode === "vehicle_axles" ? " (e em quais tipos de eixo aparecem)" : ""}</span>
                    {(f.options ?? []).map((o, k) => (
                      <div key={o.value} className="flex flex-wrap items-center gap-2">
                        <input className="input min-w-[160px] flex-1" value={o.label}
                          onChange={(e) => campo(i, { options: f.options!.map((x, j) => (j === k ? { ...x, label: e.target.value } : x)) })} />
                        <select className="input w-32" aria-label="Ícone" value={o.icon ?? ""}
                          onChange={(e) => campo(i, { options: f.options!.map((x, j) => (j === k ? { ...x, icon: e.target.value || undefined } : x)) })}>
                          <option value="">Sem ícone</option>{ICONES.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                        {value.positions?.mode === "vehicle_axles" && (["steering", "traction", "free", "lift"] as const).map((t) => (
                          <label key={t} className="inline-flex items-center gap-1 text-gray-600">
                            <input type="checkbox" checked={!o.axleTypes?.length || o.axleTypes.includes(t)}
                              onChange={(e) => {
                                const atual = o.axleTypes?.length ? o.axleTypes : ["steering", "traction", "free", "lift"];
                                const novo = e.target.checked ? [...new Set([...atual, t])] : atual.filter((x) => x !== t);
                                campo(i, { options: f.options!.map((x, j) => (j === k ? { ...x, axleTypes: novo.length === 4 ? undefined : novo } : x)) });
                              }} /> {TIPO_EIXO[t]}
                          </label>
                        ))}
                        <button type="button" className="text-gray-400 hover:text-danger-600" aria-label="Remover opção" onClick={() => campo(i, { options: f.options!.filter((_, j) => j !== k) })}>×</button>
                      </div>
                    ))}
                    <button type="button" className="btn-ghost text-xs" onClick={() => {
                      const usados = new Set((f.options ?? []).map((o) => o.value)); let n = 1; while (usados.has(`opcao_${n}`)) n++;
                      campo(i, { options: [...(f.options ?? []), { value: `opcao_${n}`, label: `Opção ${n}` }] });
                    }}>+ Opção</button>
                  </div>
                )}
              </div>
            ))}
            <button type="button" className="btn-secondary text-sm" onClick={novoCampo}>+ Campo</button>
          </div>

          <div>
            <span className="text-xs font-medium text-gray-600">Intervenções aplicáveis <span className="font-normal text-gray-400">(nenhuma marcada = todas)</span></span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {ALL_INTERVENTIONS.map((iv: MaintenanceInterventionDto) => {
                const on = value.interventions?.includes(iv) ?? false;
                return (
                  <button key={iv} type="button" aria-pressed={on} className={`rounded-full border px-2.5 py-0.5 text-xs ${on ? "border-tm-600 bg-tm-50 text-tm-700" : "border-surface-border text-gray-600"}`}
                    onClick={() => set({ interventions: on ? value.interventions!.filter((x) => x !== iv) : [...(value.interventions ?? []), iv] })}>
                    {INTERVENTION_LABEL[iv]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-xs text-gray-600">
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!!value.requiresPhoto} onChange={(e) => set({ requiresPhoto: e.target.checked || undefined })} /> Foto obrigatória (problema ou intervenção)</label>
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!!value.requiresObservationOnFailure} onChange={(e) => set({ requiresObservationOnFailure: e.target.checked || undefined })} /> Observação obrigatória em atenção/crítico</label>
            <label className="inline-flex items-center gap-1.5"><input type="checkbox" checked={!!value.requiresBeforeAfter} onChange={(e) => set({ requiresBeforeAfter: e.target.checked || undefined })} /> Antes × depois obrigatório na intervenção</label>
          </div>
        </>
      )}
    </div>
  );
}

function Num({ rotulo, valor, onChange, texto, largura = "w-24" }: {
  rotulo: string; valor: number | string | undefined; onChange: (v: number | string | undefined) => void; texto?: boolean; largura?: string;
}) {
  return (
    <label className="block"><span className="text-gray-500">{rotulo}</span>
      <input className={`input ${largura}`} inputMode={texto ? "text" : "decimal"} maxLength={texto ? 12 : 12} value={valor ?? ""}
        onChange={(e) => {
          const t = e.target.value;
          if (texto) return onChange(t || undefined);
          const n = Number(t.replace(",", "."));
          onChange(t.trim() === "" || !Number.isFinite(n) ? undefined : n);
        }} />
    </label>
  );
}
